import { useMemo } from 'react';
import {
	Home,
	Calendar,
	BarChart2,
	Settings,
	BookOpen,
	Trophy,
	CircleUserRound,
	User as UserIcon,
} from 'lucide-react';
import { ROUTES } from '@/constants';
import ReferIcon from '@/assets/images/refer.svg?react';
import type { NavigationItem, User } from '@/types/layout.types';
import { UserRole } from '@sportspredict/shared';

interface UseNavigationConfigReturn {
	bottomNav: NavigationItem[];
	navigation: NavigationItem[];
}

export function useNavigationConfig(
	user: User | null | undefined,
	isMobile: boolean
): UseNavigationConfigReturn {
	const isUser = user?.role === UserRole.User;
	const isAdmin = user?.role === UserRole.Admin;

	const bottomNav = useMemo<NavigationItem[]>(() => {
		return [
			{ name: 'home', href: ROUTES.HOME, icon: Home, tabName: 'Home' },
			{ name: 'events', href: ROUTES.EVENTS, icon: Calendar, tabName: 'Events' },
			{ name: 'leaderboard', href: ROUTES.LEADERBOARD, icon: Trophy, tabName: 'Leaderboard' },
			{ name: 'profile', href: ROUTES.PROFILE, icon: CircleUserRound, tabName: 'Profile' },
		];
	}, []);

	const navigation = useMemo<NavigationItem[]>(() => {
		const accountItems: NavigationItem[] = [
			{
				name: 'account',
				href: isMobile ? ROUTES.ACCOUNT : null,
				icon: Settings,
				tabName: 'Account Settings',
				isModal: !isMobile,
			},
		];

		const referItem: NavigationItem = {
			name: 'refer',
			href: ROUTES.REFER,
			icon: ReferIcon,
			tabName: 'Refer & Get Rewards',
		} as NavigationItem;

		const base: NavigationItem[] = [
			{
				name: 'howtoplay',
				href: isMobile ? ROUTES.HOW_TO_PLAY : null,
				icon: BookOpen,
				tabName: 'How To Play',
				isModal: !isMobile,
			},
		];

		if (isUser) {
			base.push(referItem);
		}

		if (user) {
			base.push(...accountItems);
		}

		if (isAdmin) {
			base.push(
				{
					name: 'markets',
					href: ROUTES.MARKETS,
					icon: BarChart2,
					tabName: 'All Markets',
				},
				{
					name: 'admin',
					href: ROUTES.ADMIN,
					icon: UserIcon,
					tabName: 'Admin',
				}
			);
		}

		return base;
	}, [isUser, isAdmin, isMobile, user]);

	return { bottomNav, navigation };
}
